import "core-js/stable";
import "regenerator-runtime/runtime";
import "./style/Index.scss";
import "oc-commons/extensions/StringExtensions";
import React from "react";
import ReactDOM from "react-dom";
import _ from "lodash";
import * as Utils from "./commons/Utils";
import { colorFromStyle, getWorldAxesFromCameraDirection } from "./commons/Utils";
import TWEEN from "@tweenjs/tween.js";
import * as Commons from "oc-commons/Commons";
import { sleep } from "oc-commons/Commons";
import * as Maths from "oc-commons/Maths";
import "oc-threejs/patched/OrbitControls.oc";
import { OrbitControlsOC } from "oc-threejs/patched/OrbitControlsOC";
import { InteractionHandler } from "./InteractionHandler";
import { Index } from "./Index";
import { Arrangement } from "./arrangement/Arrangement";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { JacoApp } from "oc-jaco3d/JacoApp";
import { DeviceDetection } from "oc-commons/DeviceDetection";
import { I18N } from "oc-I18N";
import { UndoHandler } from "oc-app/UndoHandler";
import { UniversalLoader } from "oc-threejs/loader/UniversalLoader";
import { checkMMRNotUndefined, RichMetadata } from "./RichMetadata";
import * as Scenes from "oc-threejs/Scenes";
import { Wkfg2BackendApi } from "oc-app/Wkfg2BackendApi";
import { MyModelsHelper } from "./MyModelsHelper";
import * as THREE from "three";
import {
  Box3,
  CameraHelper,
  Light,
  Mesh,
  Object3D,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  Sphere,
  Vector3,
  WebGLRenderer,
} from "three";
import { SceneNode } from "oc-scene-editor/nodes/SceneNode";
import { PNodeListMeta } from "oc-scene-editor/nodes/properties/PNodeListMeta";
import { Stage } from "oc-threejs/Stage";
import { quatEuler, v3 } from "oc-threejs/short3";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths2 from "oc-threejs/Maths2";
import { CameraPathHelper } from "oc-threejs/cameraautomatic/CameraPathHelper";
import { CameraTuple } from "oc-threejs/cameraautomatic/CameraTuple";
import { easeInOut } from "oc-threejs/cameraautomatic/CameraPath";
import * as Dialogs from "oc-ui/dialogs/Dialogs";
import { useAlertForDialogs } from "oc-ui/dialogs/Dialogs";
import * as Debugs from "oc-threejs/Debugs";
import * as Textures from "oc-threejs/Textures";
import { Urls } from "oc-commons/Urls";
import { SnapshotRenderer } from "oc-threejs/SnapshotRenderer";
import { CameraPathRunner } from "oc-threejs/cameraautomatic/CameraPathRunner";
import { setTentantStyle } from "./TenantStyle";
import { registerDialogsErrorHandler } from "oc-ui/dialogs/DialogsErrorHandler";
import { Segment } from "./arrangement/Segment";
import { filterTests, runTests, slurpTests } from "oc-e2e-testrunner";
import { ElementSnapshoter } from "./components/sceneeditor/ElementSnapshoter";
import Matomo from "oc-app/Matomo";
import { filterPathIntersection, findPathIntersection } from "oc-app/metadata/GraphMetadata";
import { isCookieAllowed } from "./components/Cookiebanner";
import { FormType, Grundriss } from "./arrangement/Grundriss";
import {
  generierePdfBestellseitenBilderUndAnzahlen,
  renderBlobOnWhiteBackground,
} from "./arrangement/pdfBestellseitenFunctions";
import domtoimage from "dom-to-image";
import { GrundrissZeichnung } from "./components/grundriss/GrundrissZeichnung";
import { coordinateSystem } from "./components/grundriss/CoordinateSystem";
import { ScreenUtils } from "oc-threejs/ScreenUtils";
import { Metamodell, MModelMaterialRelation } from "../metamodell";
import { NodeMaterialFactory } from "oc-scene-editor/nodes/NodeMaterialFactory";
import { MessageManager } from "./commons/Messages";
import {
  showSchiebetorSkizzeInNeuemTab,
  showSchiebetorFundament as showSchiebetorFundamentInNeuemTab,
} from "./arrangement3d/schiebetor/schiebetorSkizze";
import type { ZElement } from "./arrangement/ZElement";

// ACHTUNG! Das hier macht Animationen langsam! Ist aber nützlich zum debuggen!
if (location.hostname === "localhost") {
  // Debugs.debugFishyVectors();
}

self["THREE"] = THREE;
self["Dialogs"] = Dialogs;
self["Textures"] = Textures;
self["Scenes"] = Scenes;
self["filterPathIntersection"] = filterPathIntersection;
self["findPathIntersection"] = findPathIntersection;
self["Maths2"] = Maths2;

export class App extends JacoApp {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  controls: OrbitControlsOC;
  sceneNode: SceneNode;
  nightMode = false;

  pages = Commons.slurpDirectory(require.context("./pages"));
  pageSequence = ["Inspiration", "Zauneditor", "Grundriss"];
  pageIndexHiddenAfter = 0;
  pageIndex = -1;
  pageClass: React.ComponentClass;
  // zwingt React dazu, die Page neu zu zeichnen und an React vorbei vorgenommene Änderungen zu überschreiben
  pageShowCounter = 0;
  pageInstanceCounter = 0;
  pageInstance: React.Component & {
    pageNeeds: Partial<{
      scene: boolean;
      grundriss: boolean;
      pageTypeForCssAnimation: number;
      background: boolean;
      form: boolean;
    }>;
    fadedIn: boolean;
  };
  noMocksMode: boolean | null;
  fastMode: boolean | null;
  debugMode: boolean | null;
  hideWarnungen: boolean | null;
  materials: NodeMaterialFactory;
  laengeParameter: number;
  groundRaster: Object3D;
  shadowPlane: Object3D;
  pageName: string | null;
  pageNameFromParam: string | null;
  formName: FormType | null;
  zaunartName: string | null;
  inspirationName: string | null;
  tutorialParam: boolean;

  interactionHandler: InteractionHandler;
  arrangement: Arrangement;
  richMetadata: RichMetadata;

  isDirty = false;
  renderLooping = false;
  isDirtyUntil = 0;

  additionalCanvasBlobs: { fileName: string; blob: Blob }[] = [];

  metadata: Metamodell;
  autoSave: boolean;
  urlParams: URLSearchParams;
  pageHistory: { name: string; newURL: string }[];
  testmode: boolean;
  runningTests = false;

  // finden sich als css props wieder, faktoren für responsivness-skalierung
  responsiveFactorWide = 1;
  responsiveFactorNarrow = 1;

  userActionLog: ({ page: string; time: number } & Record<string, any>)[] = [];

  logStückliste = false;

  cookieStorageKey = "cookieBanner";
  joyrideTourCookieStorageKey = "joyrideTourStep";
  defaultJoyrideTourCookieState = { grundriss: 0, zauneditor: 0 };

  ausrichtungBilderMap: Record<string, string> = {};

  logMessagesForTests: {
    text: string;
    type: "info" | "warning" | "error";
    count: number;
    onClose?: () => void;
  }[] = [];

  isTorkonfigurator = false;

  lightCameraHelper: Object3D;
  boxHelper: Object3D;
  messages: MessageManager;

  constructor(container: HTMLElement, configurationSettings: any) {
    super("Zaunplaner", container, configurationSettings);
  }

  async _appInitialize(settings: any) {
    // wir warten einen Moment, damit die Initialisierung aller Memembers abgeschlossen ist
    await Commons.sleep(1);

    registerDialogsErrorHandler();

    // _appInitialize ist der früheste Einstiegspunkt in JacoApp für die Initialisierung der App.
    // Hier wird eine davon abweichende eigene Implementierung verwendet, um .. erstmal alles unter Kontrolle zu haben,
    // und explizit alle Schritte zu sehen und umsortieren zu können

    this.parseParams();

    settings.undo = { enabled: true };

    this._appInitializeFromAbstractApp(settings);

    // Responsive i18n Texte verwenden, wenn Breite von 1250px unterschritten wird
    I18N.setResponsiveBreakpoint(1250);
    setTentantStyle(this.settings);

    this.setGlobals();
    this.initReadyEventTrigger();
    this.initUI();

    const code = Urls.urlParam("Code");
    this.on("loadedConfiguration", () => {
      // kann insbesondere nach einem restore passieren
      console.log("loadedConfiguration");
    });

    this.on("changeSelectedObjects", async () => {
      await Commons.sleep(10);
      // die Auswahl-Linien den Kreismenus erfordern eine Wartezeit...
      this.updateUI();
      this.trigger("changeZaunelement");
    });

    await this.loadInitialConfigData();
    this.maybeSetAutosaveAndRestore();
    this._maybeCorrectInspirationName();

    if (APP.configuration?.Masskonfigurator && !this.isTorkonfigurator) {
      Commons.delayUntil(
        () => this.showTorkonfiguratorUmleitung(APP.configuration?.Code),
        () => !useAlertForDialogs
      );
    }

    this.on("cookiesSaved", () => {
      if (isCookieAllowed(this.settings.matomo?.cookieName ?? "matomo", this.cookieStorageKey)) {
        Matomo.init(this.settings.matomo ?? {});
        Matomo.registerTrackingErrorHandler();
      }
    });

    this.initStage();

    this.initInteractionHandler();
    this.interactionHandler.setSceneDroppable();

    this.addOnUnloadWarning();

    this.initControls();
    // console.time("SceneNode");
    this.initSceneNode();
    // console.timeEnd("SceneNode");

    // this.sceneImporter.setEnvironment();

    this.initArrangement();
    this.arrangement.visible = false;
    this.arrangement.jaco3d.visible = false;
    APP.on("loadedInitialConfiguration", () => {
      if (!Commons.isEmpty(this.configuration.Code)) {
        // fixme eigenartigerweise muss hier 2x Update3d stehen, sonst ist evtl. das Backup unterschiedlich im Zauneditor isBackupForZauneditorComparisonSame
        this.arrangement.update3d();
        this.arrangement.update3d();
        this.arrangement.storeBackupForZauneditorComparison();
      }
    });
    this.loadLocalStorage();
    this.loadInitialConfigurationFromApp();
    this.arrangement.update3d();
    window.addEventListener("popstate", (event) => this.onPopState(event));
    this.addResizeStartEndEventListener();
    // await this.arrangement.preloadModelsData();
    this.dirty();

    this.maybeRunTests();
  }

  createWkfgBackendApi() {
    return new Wkfg2BackendApi(
      this.settings.wkfgBackendUrl,
      this.createWkfgTypeConverter(),
      async ({ metadata, metadataPlain }) => {
        this.richMetadata = new RichMetadata(metadata, metadataPlain);
        await this.richMetadata.init();
        return metadata;
      }
    );
  }

  onPopState(event: PopStateEvent) {
    if (this.resolvePageName(event.state)) {
      this.showPage(event.state, { historyHandling: null });
    }
  }

  loadAppData() {
    // wird nicht verwendet, alles in _appInitialize
  }

  initApp() {
    // wird nicht verwendet, alles in _appInitialize
  }

  createArrangement() {
    return new Arrangement();
  }

  dirty() {
    // console.log("APP.dirty()");
    super.dirty();
  }

  createDefaultConfiguration() {
    const grundriss = new Grundriss();
    if (!APP.testmode) {
      const formName: FormType =
        this.formName ?? (this.pageNameFromParam == "Zauneditor" ? "Strecke" : "Leer");
      const seitenLängen = grundriss
        .getStandardFormSeitenLängen(formName)
        .map((a) => this.laengeParameter ?? a);
      grundriss.setStandardForm(formName, seitenLängen);
    }
    return {
      Json: {
        grundriss: {
          ...grundriss.toJSON(),
        },
        segmente: grundriss.kanten.map((kante) => ({
          verteilungsstrategieAuswahlwert: "StandardLinks",
          breite: Maths2.distance([kante.ecken[0].punkt, kante.ecken[1].punkt]),
          partsAndPfosten: [],
        })),
        inspiration: this.inspirationName,
        zaunart: this.zaunartName,
        pageName: this.pageSequence[0],
        grundrissDialogOpen: true,
      },
    };
  }

  createUndoHandler() {
    const undoHandler = new (class extends UndoHandler {
      isEqualConfiguration(lastConfig: any, newConfig: any) {
        // if (!Commons.isEqualEpsilon(lastConfig,newConfig)) {
        //     console.log('**deepDiff',Commons.deepDiff(lastConfig,newConfig),lastConfig,newConfig);
        // }
        return Commons.isEqualEpsilon(lastConfig, newConfig);
      }

      getStageAsConfigurationForUndo() {
        const configuration = APP.getStageAsConfiguration();
        const json = configuration?.Json;
        if (json == null) {
          return null;
        }
        delete json.pageName;
        delete json.zauneditorVisited;
        delete json.elementMeta;
        delete json.füllungAuswahlwert;
        delete json.massAuswahlwert;
        return configuration;
      }
    })();
    return undoHandler;
  }

  setGlobals() {
    (self as any).Utils = Utils;
  }

  parseParams() {
    const params = (this.urlParams = new URLSearchParams(window.location.search));
    this.pageNameFromParam = params.get("page");
    this.fastMode = params.get("fastMode") != null;
    this.formName = ["Leer", "Rechteck", "Strecke", "LForm"].find(
      (s) => s.toLowerCase() == params.get("form")?.toLowerCase()
    ) as FormType;
    const laenge =
      params.get("laenge") ?? params.get("Laenge") ?? params.get("länge") ?? params.get("Länge");
    this.laengeParameter = laenge != null ? parseFloat(laenge) : null;
    this.debugMode = params.get("debugMode") != null;
    this.zaunartName = params.get("zaunart") ?? "Sichtschutz";
    this.inspirationName = params.get("inspiration");
    this.testmode = params.get("test") != null;
    this.tutorialParam = params.get("tutorial") != "false";
  }

  _maybeCorrectInspirationName() {
    if (this.inspirationName == null) {
      const defaultDetailSerie = this.metadata.DetailSerie_Auswahlwert.find(
        (detailSerie) => detailSerie.Voreinstellung
      );
      const inspirationen = this.metadata.Inspiration.filter(
        ({ DetailSerie }) => DetailSerie.Auswahlwert == defaultDetailSerie
      );
      const inspiration =
        inspirationen.find(({ Inspiration }) => Inspiration.Voreinstellung)?.Inspiration ??
        APP.richMetadata.findInspiration("Inspiration SYSTEM WPC CLASSIC Anthrazit")?.Inspiration;
      this.inspirationName = inspiration?.Key;
      if (this.richMetadata.findInspiration(this.inspirationName) == null) {
        this.inspirationName = _.first(this.metadata.Inspiration)?.Inspiration?.Key;
      }
    }
  }

  // ---------------------------------------------------------
  // Stage
  // ---------------------------------------------------------

  setStageFromConfiguration(
    configuration: any,
    pageName: string = null,
    setStageAfterUndo = false
  ) {
    super.setStageFromConfiguration(configuration);
    pageName =
      pageName ?? this.pageNameFromParam ?? configuration?.Json?.pageName ?? this.pageSequence[0];
    if (!this.isTorkonfigurator && pageName == "Masseditor") pageName = "Zauneditor";
    if (!setStageAfterUndo) {
      this.showPage(pageName, {
        historyHandling: "push",
        pageEnteredInSequence: this.pageName == _.first(this.pageSequence),
      });
    }
  }

  showTorkonfiguratorUmleitung(code: string) {
    const redirectUrl = APP.settings.massConfiguratorUrl + "?Code=" + code;
    Dialogs.showDialog({
      content: (
        <div className="umleitungDialog">
          <div
            className="flex-column flex-align-center"
            dangerouslySetInnerHTML={{ __html: I18N("Torkonfigurator Umleitung") }}
          ></div>
        </div>
      ),
      buttonAndActionList: [
        {
          label: I18N("Zum Torkonfigurator wechseln"),
          primary: true,
          action: () => {
            window.location.href = redirectUrl;
          },
        },
        {
          label: I18N("Hier bleiben"),
          primary: true,
          action: () => {},
        },
      ],
    });
  }

  getSceneBackgroundObjects() {
    return Scenes.filter(this.scene, (x) => (x as any).isSceneBackground);
  }

  initStage(envName: string = null) {
    super.initStage(envName);
    APP.renderer = STAGE.renderer;
    APP.scene = STAGE.scene;
    APP.scene.visible = false;
    APP.camera = STAGE.camera;
    APP.controls = STAGE.controls;
    this.renderer.setClearColor(Utils.colorFromStyle("--color-background-2"));
    const stageElement = document.querySelector(".stage");
    stageElement.prepend(this.renderer.domElement);
    STAGE.setRendererToSize();
    STAGE.addContainerResizeListener(stageElement);
    const backgroundChange = () => {
      const backgroundObjects = this.getSceneBackgroundObjects();
      backgroundObjects.forEach((o) => (o.renderOrder = 9999));
      const backgroundVisible = this.pageInstance?.pageNeeds?.background === true;
      backgroundObjects.forEach((o) => (o.visible = backgroundVisible));
    };
    APP.on("showPage", backgroundChange);
    APP.on("SceneBackgroundChange", backgroundChange);
    APP.on("ShadowCamera", () => this.resetShadowPlane());
    APP.on("shadowLightPositionChanged", () => {
      if (this.arrangement?.grundriss) {
        const { box3 } = this.getShadowBox3();
        this.setShadowToBoundingBox(box3);
      }
    });
  }

  createStage() {
    return new (class extends Stage {
      createLights(name = STAGE.settings.lightSettingsName) {
        return [];
      }
    })(this.settings.stage);
  }

  initSceneNode() {
    const sceneNode = (this.sceneNode = new (class extends SceneNode {
      constructor() {
        super();
        const metaclasses = Commons.slurpDirectory(
          require.context("./components/sceneeditor/nodes/metadata/", true)
        );
        this.metadataNodeClasses = { ...this.metadataNodeClasses, ...metaclasses };
      }

      createProperties() {
        return super.createProperties().concat([
          {
            group: "Auswahlwert Bilder",
          },
          new (class extends PNodeListMeta<any> {
            constructor(args) {
              super(args);
              const filter1 =
                APP.metamodel.tables
                  .find(({ name }) => name == "Auswahlwert")
                  .columns.find(({ name }) => name == "Auswahlbild").enabledIfFunction ??
                ((_) => true);
              const filter2 =
                APP.metamodel.tables
                  .find(({ name }) => name == "Auswahlwert")
                  .columns.find(({ name }) => name == "Thumbnail").enabledIfFunction ??
                ((_) => true);
              const filter = (row) => filter1(row) || filter2(row);
              this.rows = this.rows
                .filter(filter)
                .toSorted((a: any, b: any) =>
                  a.Auswahl.Key < b.Auswahl.Key
                    ? -1
                    : a.Auswahl.Key > b.Auswahl.Key
                      ? 1
                      : a.Key < b.Key
                        ? -1
                        : 1
                );
            }
          })({
            name: "Auswahlwert",
            label: null,
            node: this,
            showInEditor: (p) => true,
          }),
          {
            group: "Element-Thumbnail-Creator",
          },
          {
            render: ElementSnapshoter,
          } as any,
        ]);
      }
    })());
    this.materials = sceneNode.createMaterialsFactory();
    UniversalLoader.setMaterialFactory(this.materials);
    sceneNode.init();
    this.setMaterialsFromTenant();
    this.resetStage();
  }

  setMaterialsFromTenant() {
    const [
      materialSelect,
      materialShowEmptySpaces,
      materialShowEmptySpacesSelect,
      Warnung,
      selectLine,
      materialSelectWireframe,
      materialPartHover,
    ] = [
      "materialSelect",
      "materialShowEmptySpaces",
      "materialShowEmptySpacesSelect",
      "Warnung",
      "selectLine",
      "materialSelectWireframe",
      "materialPartHover",
    ].map((name) => this.materials.create(name));
    [materialSelect, selectLine, materialSelectWireframe].forEach((m) =>
      (m as any).color.copy(colorFromStyle("--color-interactive-2"))
    );
    (Warnung as any).color.copy(colorFromStyle("--color-error"));
    [materialShowEmptySpacesSelect, materialShowEmptySpaces].forEach((m) =>
      (m as any).color.copy(colorFromStyle("--color-leerraum"))
    );
    (materialPartHover as any).color.copy(colorFromStyle("--color-part-hover"));
  }

  resetStage() {
    // this.scene.children.filter((x) => x !== this.camera && x !== this.lights).forEach((x) => Scenes.remove(x));
    this.resetCamera();
  }

  _zoomCameraDirection = v3(0, 0.3, 1).normalize();
  _lastSelectedNormalWorld = this._zoomCameraDirection;

  zoomCameraToPart(part: any, linear = false) {
    APP.trigger("controlsStart");
    const targetJaco = part ?? APP.arrangement;
    const isInSegment = part?.parent instanceof Segment;
    let zoom = 1;
    let lookDirection;

    if (part) {
      if (isInSegment) {
        const isFlipped = part.segment?.flippedNormal === true;
        let cameraDirection = this._zoomCameraDirection.clone();

        if (isFlipped) {
          cameraDirection.x = -cameraDirection.x;
          cameraDirection.z = -cameraDirection.z;
        }

        lookDirection = cameraDirection.applyQuaternion(targetJaco.parent.getWorldQuaternion());
      } else {
        lookDirection = getWorldAxesFromCameraDirection()
          .zn.add(v3(0, 0.3, 0))
          .normalize();
      }
      this._lastSelectedNormalWorld = lookDirection;
      zoom = 1.5;
    } else {
      lookDirection = this._lastSelectedNormalWorld;
    }
    const box = targetJaco.getBox();
    box.applyMatrix4(targetJaco.matrixWorld);
    box.max.y = Math.max(3, box.max.y);
    const pathHelper = new CameraPathHelper();
    const [target, position] = STAGE.getCameraConfigAsZoomToBox(box, lookDirection, zoom);
    const path = linear
      ? pathHelper.linearPathTo(new CameraTuple(position, target), easeInOut)
      : pathHelper.dronePathTo(new CameraTuple(position, target), 5, easeInOut);

    const pathDistance = path.getAt(0).position.distanceTo(path.getAt(1).position);
    const duration =
      (linear || path.isLinear ? 0 : 3500) * Maths.mapToUnitInterval(0, pathDistance, 30) + 600;

    return pathHelper.run(path, duration).then(() => {
      APP.trigger("controlsEnd");
    });
  }

  defaultCameraPosition = v3(0, 5, 0);
  defaultCameraUp = v3(0, 0, -1);

  resetCamera() {
    // das hier ist ein hard reset - nicht geeignet für innerhalb des Zauneditors!
    // dort besser APP.zoomCameraToJaco(null)
    this.camera.position.copy(this.defaultCameraPosition);
    this.camera.zoom = 1;
    this.camera.lookAt(0, 0, 0);
    this.camera.up.copy(this.defaultCameraUp);
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.setViewOffset(w, h, 0, -80, w, h);
    if (this.controls) {
      this.controls.target.set(0, 0, 0);
      this.controls.resetFromObjectAndTarget();
    }
  }

  toggleDaytime() {
    this.nightMode = !this.nightMode;
    const sun = STAGE.getLightByName("Sonne")[0];
    const ambient = STAGE.getLightByName("Ambient")[0];
    const to = { intensity: this.nightMode ? 0.2 : 1.0 };
    const tween = new TWEEN.Tween(ambient);
    tween.to(to, APP.settings.dayNightAnimationDuration ?? 1000);
    tween.onUpdate(() => {
      STAGE.dirty();
    });
    tween.start(STAGE.tweenStartNowTime);
    tween.onComplete(() => {
      APP.dirty();
    });
    this.sceneNode.environmentJson.environmentNode.toggleDayNight();
    this.sceneNode.init();
    STAGE.dirty();
  }

  getShadowBox3() {
    const size2 = this.arrangement.grundriss.size.clone().multiplyScalar(2);
    const box3 = Maths3.boxFromGridMinAndSize(
      v3(0.5, 0, 0.5),
      v3(size2.x * 0.75, 2, size2.y * 0.75)
    );
    return { size2, box3 };
  }

  resetShadowPlane() {
    if (!this.arrangement?.grundriss) {
      return;
    }
    const { size2, box3 } = this.getShadowBox3();
    this.setShadowToBoundingBox(box3);

    Scenes.remove(this.shadowPlane);
    Scenes.remove(this.groundRaster);
    // Scenes.remove(this.groundPlane);

    const planeGeometry = new PlaneGeometry(size2.x, size2.y, 1, 1);

    const dist = 0.01;

    this.shadowPlane = new Mesh(planeGeometry, this.materials.create("shadowPlane"));
    this.shadowPlane.name = "shadowPlane";
    (this.shadowPlane as any).isSceneBackground = true;
    this.shadowPlane.receiveShadow = true;
    this.shadowPlane.position.y = 1 * dist;
    this.shadowPlane.renderOrder = 2;
    this.shadowPlane.quaternion.copy(quatEuler(-90, 0, 0));
    this.scene.add(this.shadowPlane);

    if (this.debugMode) {
      Debugs.wireframe(this.shadowPlane);
    }

    APP.trigger("SceneBackgroundChange");
  }

  setShadowToBoundingBox(box: Box3) {
    Scenes.filter(
      this.scene,
      (x) => x instanceof Light && x.castShadow && (x as any).target
    ).forEach((lightObject) => {
      const light = lightObject as any; // Cast to any or DirectionalLight
      if (_.isEqual(light.position, light.target.position)) {
        light.target.position.add(v3(0.01, 0.01, 0.01));
      }
      Scenes.setDirectionalLightShadowToCoverBoundingBox(light, box);

      if (this.debugMode) {
        Scenes.remove(this.lightCameraHelper);
        this.lightCameraHelper = new CameraHelper(light.shadow.camera);
        this.scene.add(this.lightCameraHelper);

        Scenes.remove(this.boxHelper);
        this.boxHelper = Debugs.box(box, 0xff0000, true);
      }
    });
  }

  initArrangement() {
    this.arrangement = new Arrangement();
    this.update3d();
  }

  update3dPlain() {
    // const t0 = performance.now();
    this._currentUpdateId++;
    this.arrangement.update3d();
    // doppeltes update3d behebt einige Darstellungsfehler
    // this.arrangement.update3d();
    this._maybeAddArrangement3dToScene();
    this._traverseUpdate3d(this.arrangement3d);
    this._cleanUp();
  }

  initInteractionHandler() {
    this.interactionHandler = new InteractionHandler(document.querySelector(".stage"));
    // das war vorher im Wait, ist jetzt sofort nach dieser Methode!
    // Commons.sleep(1000).then(() => this.interactionHandler.setSceneDroppable());
  }

  initControls() {
    this.controls = STAGE.orbitcontrols;
    this.controls.addEventListener("start", () => {
      this.controls.autoRotate = false;
      APP.trigger("controlsStart");
    });

    this.controls.addEventListener("end", () => APP.trigger("controlsEnd"));
  }

  getScreenPositionOf(jaco: Jaco) {
    return this.getScreenCircleOf(jaco).center;
  }

  getScreenCircleOf(jaco: Jaco) {
    const worldBox = jaco.getBox().applyMatrix4(jaco.matrixWorld);
    const worldSphere = worldBox.getBoundingSphere(new Sphere());
    const sc = new ScreenUtils();
    const circle = sc.worldSphereToScreen(worldSphere);
    circle.center.floor();
    circle.radius = Math.round(circle.radius);
    return circle;
  }

  async tween(update: (t: number) => void, duration = 3000, easing = TWEEN.Easing.Quintic.InOut) {
    if (this.fastMode) {
      await Commons.sleep(1);
      update(1);
      return;
    }
    return new Promise((resolve, reject) => {
      const time = { t: 0 };
      const renderLoopingBefore = STAGE.renderLooping;
      STAGE.renderLooping = true;
      const tween = new TWEEN.Tween(time)
        .to({ t: 1 }, duration)
        .easing(easing)
        .onUpdate(() => update(time.t))
        .onComplete(() => {
          update(1);
          resolve(null);
          STAGE.renderLooping = renderLoopingBefore;
          STAGE.setDirtyForMs(600);
        })
        .start();
    });
  }

  stopAllAnimations() {
    TWEEN.removeAll();
    CameraPathRunner.stopAll();
  }

  get modelsHelper() {
    if (this._modelsHelper == null) {
      this._modelsHelper = new MyModelsHelper();
    }
    return this._modelsHelper;
  }

  checkMMRNotUndefined(mmr: MModelMaterialRelation): MModelMaterialRelation {
    return checkMMRNotUndefined(mmr);
  }

  // ---------------------------------------------------------
  // Page
  // ---------------------------------------------------------

  updateUIDebounced = _.debounce(() => this.updateUI(), 20);
  configurationChangedDebounced = _.debounce(() => this.trigger("changedConfiguration"), 600);

  initUI() {
    ReactDOM.render(<Index />, this.container);
    this.ui = { dirty: () => this.updateUIDebounced() };
    APP.on("afterUpdate3d", () => this.configurationChangedDebounced?.());
    APP.on("undoHistoryChanged", () => APP.updateUIDebounced());
  }

  updateUI() {
    const t0 = performance.now();
    this.trigger("beforeUpdateUI");
    ReactDOM.render(<Index />, this.container);
    const t1 = performance.now();
  }

  resolvePageName(name: string) {
    if (this.pages[name]) {
      return name;
    }
    return this.pageSequence[0];
  }

  isCurrentPage(pageName: string) {
    return pageName == this.pageName;
  }

  getPageIndex() {
    return this.pageSequence.indexOf(this.pageName);
  }

  isNextPageJumpable() {
    return this.pageIndex + 1 <= this.pageIndexHiddenAfter;
  }

  isPageJumpable(pageName: string) {
    return true;
    // const pageIndex = this.pageSequence.indexOf(pageName);
    // return pageIndex - 1 <= this.pageIndexHiddenAfter && pageIndex != APP.pageIndex;
  }

  async showNextPage() {
    await Commons.sleep(100);
    await this.showPage(this.pageSequence[this.pageIndex + 1], {
      historyHandling: "push",
      pageEnteredInSequence: true,
    });
  }

  // createResetCameraAnimation(zielCameraPosition, zielCameraTarget, zielCameraUp) {
  //   const zoomes = [this.camera.zoom, 1];
  //   const positions = [this.camera.position.clone(), zielCameraPosition.clone()];
  //   const targets = [this.controls.target.clone(), zielCameraTarget.clone()];
  //   const ups = [this.camera.up.clone(), zielCameraUp.clone()];
  //   this._lastSelectedNormalWorld = this._zoomCameraDirection;
  //
  //   return (t) => {
  //     this.camera.zoom = THREE.MathUtils.lerp(...zoomes, t);
  //     this.camera.position.lerpVectors(...positions, t);
  //     this.camera.up.lerpVectors(...ups, t);
  //     this.controls.target.lerpVectors(...targets, t);
  //     this.controls.resetFromObjectAndTarget();
  //   };
  // }
  //
  // createResetShapeAnimation() {
  //   const form = APP.arrangement.form;
  //   this.formReposition();
  //   const formShapeSize = form.getShapeSize();
  //   const screenUtils = new ScreenUtils();
  //   let y = screenUtils.getDistanceToCameraWhereWorldUnitsEqualsPixel(
  //     Utils.shrinkRectByFactor(screenUtils.canvasRect, 0.2),
  //     formShapeSize
  //   );
  //   return this.createResetCameraAnimation(
  //     v3(0, y, 0),
  //     this.defaultFormPosition,
  //     this.defaultCameraUp
  //   );
  // }

  async showPage(pageName: string, { historyHandling = null, pageEnteredInSequence = false } = {}) {
    if (pageName == undefined) {
      debugger;
    }
    console.log("page", pageName);
    this.stopAllAnimations();
    APP.dirty();
    const pageInstanceBefore = this.pageInstance;
    const pageBeforeNeeds = pageInstanceBefore?.pageNeeds ?? {};
    if (this.pageInstance != null) {
      if ((this.pageInstance as any).exit) {
        await (this.pageInstance as any).exit(pageName);
      }
      // const page = document.querySelector(".page");
      // page.classList.add('hidden');
    }
    this.pageName = this.resolvePageName(pageName);
    this.pageIndex = this.getPageIndex();
    this.pageClass = this.pages[this.pageName];
    this.pageShowCounter++;
    document.title = (this.settings.tenant.SiteTitle ?? "Easy-Zaunplaner ") + " | " + this.pageName;
    await Commons.sleep(10);
    if (historyHandling != null) {
      this.handleHistory(this.pageName, historyHandling);
    }
    const cfg = document.querySelector("#cfg-app");
    const classes = [
      ...new Set(
        [...cfg.classList]
          .filter((s) => !s.startsWith("cfg-page-"))
          .concat(["cfg-page-" + this.pageName, APP.settings.designABC, APP.settings.designABCTabs])
      ),
    ];
    cfg.className = classes.join(" ");
    const pageInstanceCounterBefore = this.pageInstanceCounter;
    this.updateUI();
    await Commons.sleepUntil(() => this.pageInstanceCounter > pageInstanceCounterBefore);
    this.pageIndexHiddenAfter = Math.max(this.getPageIndex(), this.pageIndexHiddenAfter);
    this.trigger("showPage");
    await this.satisfyPageNeeds({ pageEnteredInSequence, pageBeforeNeeds } as any);
    await (this.pageInstance as any).init({ pageEnteredInSequence, pageBeforeNeeds });
    this.trigger("pageInitialized");
  }

  async satisfyPageNeeds({
    pageEnteredInSequence,
    pageBeforeNeeds,
  }: {
    pageEnteredInSequence: boolean;
    pageBeforeNeeds: Record<string, any>;
  }) {
    let needsShapeAnimation = false;

    const satisfyPageNeedsCssAnimationDuration = async () => {
      const needsCssAnimationDuration =
        pageBeforeNeeds != null &&
        this.pageInstance.pageNeeds.pageTypeForCssAnimation !==
          pageBeforeNeeds?.pageTypeForCssAnimation;
      if (needsCssAnimationDuration) {
        await Commons.sleep(550);
      }
    };

    const satisfyPageNeedsScene = () => {
      const needsScene = this.pageInstance.pageNeeds.scene == true;
      this.scene.visible = needsScene;
      this.arrangement.visible = false;
      if (this.arrangement.jaco3d) this.arrangement.jaco3d.visible = false;
      this.controls.enabled = false;
      APP.controls.autoRotate = false;
    };

    const satisfyPageNeedsSceneBackground = () => {
      APP.trigger("SceneBackgroundChange");
    };

    const satisfyPageNeedsGrundriss = () => {
      const needsGrundriss = this.pageInstance.pageNeeds.grundriss == true;
      if (needsGrundriss) {
        needsShapeAnimation = true;
      }
    };

    satisfyPageNeedsScene();
    await satisfyPageNeedsCssAnimationDuration();
    satisfyPageNeedsSceneBackground();
    satisfyPageNeedsGrundriss();

    APP.dirty();

    // if (needsShapeAnimation) {
    //   await Commons.sleep(300);
    //   await APP.tween(this.createResetShapeAnimation(), 1000);
    // }
  }

  handleHistory(pageName: string, historyHandling: string) {
    const params = new URLSearchParams(window.location.search);
    params.set("page", pageName);
    if (this.configuration.Code) {
      params.set("Code", this.configuration.Code);
    } else {
      params.delete("Code");
    }
    const newURL = window.location.href.split("?")[0] + "?" + params.toString();
    const method = historyHandling == "push" ? "pushState" : "replaceState";
    history[method](pageName, "", newURL);
    // for debugging
    this.pageHistory = this.pageHistory ?? [];
    if (historyHandling == "replace") {
      this.pageHistory.pop();
    }
    this.pageHistory.push({ name: pageName, newURL });
  }

  pageFadeIn(wait = true) {
    this.pageInstance.fadedIn = true;
  }

  pageFadeOut(wait = true) {
    Utils.querySelectorAll(".page-fade-out-on-exit").forEach((element, index) => {
      element.classList.add("fade");
      element.classList.add("hidden");
    });
    this.pageInstance.fadedIn = false;
  }

  // ---------------------------------------------------------
  // LocalStorage
  // ---------------------------------------------------------

  saveLocalStorage() {
    localStorage["explicitSaved"] = JSON.stringify(this.getStageAsConfiguration());
  }

  loadLocalStorage() {
    const configuration = localStorage["explicitSaved"];
    if (configuration) {
      this.configuration = JSON.parse(configuration);
    }
  }

  clearLocalStorage() {
    delete localStorage["explicitSaved"];
  }

  get joyrideTourStep() {
    return (
      JSON.parse(localStorage.getItem(this.joyrideTourCookieStorageKey)) ??
      this.defaultJoyrideTourCookieState
    );
  }

  set joyrideTourStep(step: { grundriss: number; zauneditor: number }) {
    localStorage[this.joyrideTourCookieStorageKey] = JSON.stringify(step);
  }

  // ---------------------------------------------------------
  // Events
  // ---------------------------------------------------------

  trigger(event: string, ...args: any[]): this {
    // console.log('trigger',event,args);
    super.trigger(event, ...args);

    const methodName = Commons.getMethodNameForEvent(event);
    if (this.pageInstance != null && this.pageInstance[methodName]) {
      this.pageInstance[methodName](...args);
    }

    return this;
  }

  addResizeStartEndEventListener() {
    let lastResize = -1;
    const offResize = _.debounce(() => this.trigger("windowResizeEnd"), 300);
    this.updateResponsiveFactor();
    window.addEventListener("resize", () => {
      const now = Date.now();
      if (now - lastResize > 300) {
        this.trigger("windowResizeStart");
      }
      lastResize = now;
      offResize();
      this.updateResponsiveFactor();
    });
    STAGE.on("setRenderSize", () => this.trigger("setRenderSize"));
  }

  updateResponsiveFactor() {
    this.responsiveFactorWide = Math.min(
      Maths.mapToUnitInterval(1000, window.innerWidth, 1920),
      Maths.mapToUnitInterval(700, window.innerHeight, 1440)
    );
    this.responsiveFactorNarrow = Math.min(
      Maths.mapToUnitInterval(1000, window.innerWidth, 1100),
      Maths.mapToUnitInterval(700, window.innerHeight, 1000)
    );
    document.documentElement.style.setProperty(
      "--responsive-factor-narrow",
      this.responsiveFactorNarrow + ""
    );
    document.documentElement.style.setProperty(
      "--responsive-factor-wide",
      this.responsiveFactorWide + ""
    );
  }

  async renderZoomToBox(lookDirection?: Vector3, box?: Box3, zoom?: number) {
    const width = 1200;
    const height = 800;
    const renderer = new SnapshotRenderer();
    renderer.scene = this.scene;
    renderer.camera = this.camera.clone();
    renderer.camera.fov = 40;
    renderer.setSize(width, height);
    const raster = this.scene.children.find(
      (child) => child.name === "groundRaster" && child.type === "LineSegments"
    );
    raster && (raster.visible = false);
    try {
      if (lookDirection || box || zoom) {
        const [target, position] = STAGE.getCameraConfigAsZoomToBox(
          box,
          lookDirection.clone().normalize(),
          zoom
        );
        renderer.camera.position.copy(position);
        renderer.camera.lookAt(target);
      }
      const blob = await renderer.getBlobPromise(renderer.scene as any);
      if (!blob) return blob;
      return await renderBlobOnWhiteBackground(blob);
    } finally {
      raster && (raster.visible = true);
    }
  }

  async getPdfContentBlobs() {
    const box = this.arrangement.getBox();
    const boxDistance = box.getSize(v3()).distanceTo(v3(0, 0, 0));
    // Für kleine Zaunanlagen müssen wir etwas weiter rauszoomen.
    const zoom = boxDistance < 6 ? 6 / boxDistance : 1;

    STAGE.saveCameraPos();
    const schnappschussBlob = await this.renderZoomToBox();
    const gesamtansichtBlob = await this.renderZoomToBox(v3(0, 0.3, 1), box, zoom);
    const grundrissBlob = await this.getGrundrissBlob();
    STAGE.restoreCameraPos();

    return [
      { fileName: "schnappschuss.jpg", blob: schnappschussBlob },
      { fileName: "gesamtansicht.jpg", blob: gesamtansichtBlob },
      { fileName: "grundriss.jpg", blob: grundrissBlob },
    ].filter(({ blob }) => blob != null);
  }

  async makePdfSnapshots(configurationFromServer: any) {
    // hide hoverBox during snapshot creation
    const parts = APP.arrangement.getPartsAndPfosten();
    parts.forEach((p) => {
      const hoverBox = p.jaco3d.hoverBox;
      if (hoverBox) {
        hoverBox.visible = false;
      }
      const selectBox = p.jaco3d.selectBox;
      if (selectBox) {
        selectBox.visible = false;
      }
    });

    const warningTextplanes = (APP.arrangement.warnungen ?? [])
      .map((warnung) => warnung?.jaco3d?.textplane)
      .filter((textplane): textplane is Object3D => textplane != null)
      .map((textplane) => ({ textplane, visible: textplane.visible }));
    if (!APP.isTorkonfigurator) {
      warningTextplanes.forEach(({ textplane }) => {
        textplane.visible = false;
      });
    }

    let content = [];
    try {
      content = await this.getPdfContentBlobs();
    } finally {
      warningTextplanes.forEach(({ textplane, visible }) => {
        textplane.visible = visible;
      });
    }

    this.additionalCanvasBlobs.forEach((blob) => content.push(blob));
    this.additionalCanvasBlobs = [];
    const uploadPath = "Konfiguration/" + configurationFromServer.Code;
    // Upload in batches, um phps file upload limits nicht zu überschreiten
    const batchSize = 10;
    for (let i = 0; i < content.length; i += batchSize) {
      await this.wkfgBackendApi.uploadContent(uploadPath, content.slice(i, i + batchSize));
    }
  }

  async getGrundrissBlob() {
    const camConfig = STAGE.getCurrentCameraConfig();
    const grundriss = APP.arrangement.grundriss;
    const canvasRect = { width: 1400, height: 2000 };
    const cs = coordinateSystem(grundriss, canvasRect as any);
    const su = new ScreenUtils();
    const baseWidth = 1920;
    const baseHeight = 1080;
    const widthFactor = baseWidth / window.innerWidth;
    const heightFactor = baseHeight / window.innerHeight;
    const scalingFactor = Math.min(widthFactor, heightFactor);
    const y =
      su.getDistanceToCameraWhereWorldUnitsEqualsPixel(
        canvasRect as any,
        v3(grundriss.size.x, grundriss.size.z, 0) as any,
        false
      ) * scalingFactor;
    const grundrissMiddle = grundriss.getBox().grid(v3(0.5, 0, 0.5));
    grundrissMiddle.setY(y);
    cs.setCameraTo(grundrissMiddle);
    const domElement = await Utils.reactRenderStatic(
      <GrundrissZeichnung cs={cs} hide={false} rect={canvasRect} forSnapshot={true} />
    );
    domElement.style.width = canvasRect.width + "px";
    domElement.style.height = canvasRect.height + "px";
    // appendChild verhält sich -in unserem Fall- im Firefox anders als im Chrome?
    // desshalb wird hier prepend verwendet
    document.body.prepend(domElement);
    STAGE.setCameraConfig(camConfig);
    await Commons.sleep(500);
    const blob = await domtoimage.toBlob(domElement, { bgcolor: "#fcfcfc" });
    document.body.removeChild(domElement);
    APP.dirty();
    return blob;
  }

  // ---------------------------------------------------------
  // Save
  // ---------------------------------------------------------

  /**
   * Speichert die Konfiguration.
   */
  async save(afterDecissionCB: () => void) {
    const configuration = APP.getStageAsConfiguration();
    if (configuration) configuration.Masskonfigurator = this.isTorkonfigurator;

    if (
      this._isValidationErrorAndMaybeShowError(
        "CantSaveTheConfigurationBecauseOfTheFollowingErrors"
      )
    ) {
      return;
    }

    const create = "create";
    const update = "update";
    const create_or_update = "create_or_update";

    const decision_table = {
      admin: {
        default_config: create,
        inspiration_config: create_or_update,
        admin_config: create_or_update,
        customer_config: create_or_update,
        locked_config: create_or_update,
      },
      haendler: {
        default_config: create,
        inspiration_config: create,
        admin_config: create,
        customer_config: create_or_update,
        locked_config: create_or_update,
      },
      kunde_eingeloggt: {
        default_config: create,
        inspiration_config: create,
        admin_config: create,
        customer_config: create_or_update,
        locked_config: create,
      },
      kunde_nicht_eingeloggt: {
        default_config: create,
        inspiration_config: create,
        admin_config: create,
        customer_config: create_or_update,
        locked_config: create,
      },
    };

    const role = APP.settings.userRole;

    const configurationType = this.getConfigurationType(configuration);
    let decision = decision_table[role][configurationType];

    const saveAction = async (method) => {
      afterDecissionCB?.();
      return await this.saveInternAction(configuration, method);
    };

    if (configurationType == "locked_config") {
      if (decision == create) {
        const { callback } = (await Dialogs.askQuestionPromise(
          I18N("LockedCreateANewConfiguration"),
          [
            {
              label: I18N("CreateNew"),
              primary: true,
              callback: async () => {
                configuration.Locked = false;
                return await saveAction(create);
              },
            },
            {
              key: "Cancel",
              label: I18N("Cancel"),
              callback: async () => {},
            },
          ] as any
        )) as any;
        await callback();
      } else {
        const { callback } = (await Dialogs.askQuestionPromise(I18N("LockedOverrideOrCreateNew"), [
          {
            label: I18N("CreateNew"),
            primary: true,
            callback: async () => {
              configuration.Locked = false;
              await saveAction(create);
            },
          },
          {
            label: I18N("Overwrite"),
            callback: async () => await saveAction(update),
          },
          {
            label: I18N("Cancel"),
            callback: async () => {},
          },
        ] as any)) as any;
        await callback();
      }
    } else if (decision == create_or_update) {
      const { callback } = (await Dialogs.askQuestionPromise(
        I18N("WouldYouLikeToReplaceOrAddTheConfiguration"),
        [
          {
            label: I18N("Overwrite"),
            primary: true,
            callback: async () => await saveAction(update),
          },
          {
            label: I18N("CreateNew"),
            callback: async () => await saveAction(create),
          },
        ] as any
      )) as any;
      await callback();
    } else {
      await saveAction(decision);
    }
  }

  /**
   */
  async saveInternAction(configuration: any, method: "create" | "update") {
    configuration.UserId = APP.settings.userId;

    if (method == "create") {
      configuration.id = null;
      configuration.Code = null;
      configuration.bemerkungen = null;
    }

    if (APP.settings.userRole != "admin") {
      configuration.Inspiration = false;
    }
    configuration.PreisInclMwst = APP.arrangement.gesamtpreis;
    configuration.Serie = APP.arrangement.inspiration?.DetailSerie?.Key;
    APP.trigger("beforeSaveConfiguration", configuration, method);

    APP.arrangement.getElemente().forEach((element) => {
      if (element.isMasstor || (element.isTor && element.isBestellseite)) {
        if (element.isSchiebetor && element.schiebetorOffen) {
          element.schiebetorOffen = false;
        } else {
          element.torÖffnungswinkel = 0;
        }
      }
    });
    APP.dirty();

    configuration.UserGuid = APP.settings.userGuid;

    APP.isSaving = true;
    APP.arrangement.update3d();
    // Etwas länger warten, damit die Tore für die Snapshots komplett geschlossen sind.
    await Commons.sleep(3000);
    // JSON-Snapshot nach update3d() neu erfassen, damit persistierter Stand und die
    // anschließend (in makePdfSnapshots) gerenderten PDF-Bilder garantiert denselben
    // Arrangement-Zustand widerspiegeln.
    configuration.Json = Commons.cloneDeep(APP.arrangement.toJSON());
    await generierePdfBestellseitenBilderUndAnzahlen(
      configuration.Json,
      this.additionalCanvasBlobs
    );

    APP.isSaving = false;
    APP.arrangement.update3d();

    const configurationFromServer = await this.wkfgBackendApi.saveMetadata(
      "Konfiguration",
      method,
      configuration
    );
    console.log("configurationFromServer", configurationFromServer);

    // transactional: erst jetzt, wo der Server die Config wirklich gespeichert hat, daten übertragen
    this.updateConfigurationWithUnchangedJson(configurationFromServer);

    const setBrowserUrlWithoutReload = () => {
      const configuration = APP.getStageAsConfiguration();
      const code = configuration.code ? configuration.code() : configuration.Code;
      let configuratorUrl = Urls.addOrUpdateQsParameter(window.location.href, "Code", code);
      if (!Commons.isEmpty(code) && !Commons.isEmpty(configuratorUrl)) {
        // Unter FF kann ein NS_ERROR_ILLEGAL_VALUE aufreten der nicht gecached werden
        // kann wenn der String > 640 kb ist, daher compress
        history.pushState(null, "", configuratorUrl);
      }
    };
    setBrowserUrlWithoutReload();

    await this.makePdfSnapshots(configurationFromServer);

    APP.trigger("savedConfiguration");
    this._setConfigurationCookie();
    this._setConfigurationSnapshot();
    APP.dirty();
  }

  async dummySnapshot() {
    const renderer = new SnapshotRenderer();
    renderer.setSize(600, 400);
    const imgStr = await renderer.getImage(this.scene, this.camera, "image/png");
    Debugs.showImageInWindow(imgStr);
  }

  showSchiebetorSkizze(tor: ZElement) {
    showSchiebetorSkizzeInNeuemTab(tor);
  }

  showSchiebetorFundament(tor: ZElement) {
    showSchiebetorFundamentInNeuemTab(tor);
  }

  maybeRunTests() {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("test")) {
      return false;
    }
    const testNameFilter = urlParams.get("test");
    APP.onOnce("pageInitialized", () =>
      _.delay(() => {
        // --- TEST Preconditions ---------------
        APP.settings.fadeOutDeletedElements = false;
        // --------------------------------------
        const testCases = this.getTestCases(testNameFilter);
        runTests(testCases, testNameFilter);
      }, 100)
    );
    return true;
  }

  getTestCases(testNameFilter: string) {
    const testCases = filterTests(
      slurpTests(require.context("./testsE2E", true, /.*\.(js|ts|jsx|tsx)$/)),
      testNameFilter
    );
    return testCases;
  }

  showLoadingDialog() {
    // nothing...
  }

  /**
   * Steuert den UndoHandler. Sendet eine User Aktion nach Matomo und an den GPTAssistent.
   * @param param ein Key-Value der Log-Parameter, z. B. {action: "deleteButton"}
   * @param cached wenn true (default false), unterdrückt mehrfache gleiche Events hintereinander, z.b. bei Mouse-Move
   * @param stateChange wenn false (default true), unterdrückt einen Undo-Handler-Eintrag
   */
  logUserAction(
    param: Record<string, any>,
    { cached, stateChange }: { cached?: boolean; stateChange?: boolean } = {}
  ) {
    cached = cached ?? false;
    stateChange = stateChange ?? true;
    const logKey = (v) => v?.logKey ?? v?.Key ?? v;
    if (cached) {
      const last = this.userActionLog.at(-1);
      if (last) {
        outer: {
          for (const key of Object.keys(param)) {
            if (logKey(last[key]) != logKey(param[key])) {
              break outer;
            }
          }
          return;
        }
      }
    }
    const paramAsKeys = {};
    const replaceByKey = (o) => (_.isArray(o) ? o.map(logKey) : logKey(o));
    Object.keys(param).forEach((key) => (paramAsKeys[key] = replaceByKey(param[key])));
    const logEntry = {
      page: this.pageName,
      time: Date.now(),
      ...paramAsKeys,
    };
    this.userActionLog.push(logEntry);
    // console.log("logUserAction", logEntry);

    const [action, ...rest] = Object.values(paramAsKeys)
      .filter((v) => v != null)
      .map((x) => (typeof x == "string" ? x : "" + x));
    const value = rest.join(" ");
    Matomo.event(this.pageName, action, value, undefined);
    // console.log(`Matomo.event("${this.pageName}", "${action}", "${value}")`);
    if (stateChange) {
      APP.trigger("userActionWithStateChange");
    }
    // console.log("Matomo.event", [this.pageName, action, name, value]);
  }

  /**
   * Zeigt einen Lade-Spinner an während die übergebene Funktion hardWork läuft.
   * Alternativ: Wrappe selbst mit await APP.showSpinner() und APP.hideSpinner()
   * Dafür muss die aufrufende Funktion selbst wieder als async gekennzeichnet werden.
   * @param hardWork
   * @returns {Promise<void>}
   */
  async doWorkWithSpinner(hardWork: () => void) {
    await this.showSpinner();
    hardWork();
    this.hideSpinner();
  }

  /**
   * Zeigt einen Lade-Spinner an bis hideSpinner() aufgerufen wird.
   * Nutze also await APP.showSpinner(); ...; APP.hideSpinner();
   * Dafür muss die aufrufende Funktion selbst wieder als async gekennzeichnet werden.
   * @returns {Promise<void>}
   */
  async showSpinner() {
    const loadingScreen = document.getElementById("loadingScreen");
    if (loadingScreen) {
      loadingScreen.classList.add("show");
      await sleep(50);
    }
  }

  /**
   * Versteckt den Lade-Spinner wieder, der von showSpinner angezeigt wird.
   */
  hideSpinner() {
    const loadingScreen = document.getElementById("loadingScreen");
    if (loadingScreen) {
      loadingScreen.classList.remove("show");
    }
  }
}

self["configurator"] = function (container: HTMLElement, settings: any) {
  return new App(container, settings);
};

if (DeviceDetection.isIE()) {
  alert(
    I18N(
      "Dieser Browser wird nicht mehr unterstützt. Um die Anwendung zu nutzen, verwenden Sie bitte einen aktuellen Browser, wie z.B. Edge, Firefox oder Chrome."
    )
  );
}
